/**
 * CustomersPage (§165) — a lightweight CRM over the farm's storefront buyers. Lists
 * customers with live order stats (orders / lifetime spend / last order), lets you edit
 * contact + marketing opt-in + notes, see a customer's order history, and one-click
 * import every buyer from existing storefront orders. Backend grafty-api §211.
 * Auth-gated → build-verified (tsc/eslint/vite), not preview.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, X, Download, Pencil, Trash2, Mail, Phone, Send, MessageSquare, Sparkles } from 'lucide-react'
import { customersApi, type Customer, type CustomerOrder, type CustomerReservation, type Campaign } from '../lib/api'
import { apiError } from '../lib/errors'

const card: React.CSSProperties = { background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }
const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }
const btn = (bg: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', background: bg, color: '#fff', fontSize: 12.5, fontWeight: 600 })
const iconBtn: React.CSSProperties = { display: 'inline-flex', padding: '5px 8px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-bg-card)', color: 'var(--color-text)', cursor: 'pointer', flexShrink: 0 }
const money = (n: number) => `$${(n ?? 0).toFixed(2)}`

export default function CustomersPage() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Customer | null>(null)
  const [creating, setCreating] = useState(false)
  const [viewing, setViewing] = useState<Customer | null>(null)
  const [composing, setComposing] = useState(false)
  const [texting, setTexting] = useState(false)
  const [msg, setMsg] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['customers', search],
    queryFn: () => customersApi.list(search).then(r => r.data.customers),
  })
  const customers = data ?? []
  const optedIn = customers.filter(c => c.email_opt_in && c.email).length
  const smsOptedIn = customers.filter(c => c.sms_opt_in && c.phone).length

  const remove = useMutation({
    mutationFn: (id: string) => customersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  })
  const doImport = useMutation({
    mutationFn: () => customersApi.importFromOrders(),
    onSuccess: (r) => {
      const n = r.data.imported
      setMsg(n > 0 ? `Imported ${n} customer${n !== 1 ? 's' : ''} from orders.` : 'No new customers — all order buyers are already here.')
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
    onError: (e) => setMsg(apiError(e, 'Import failed')),
  })

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={20} /> Customers
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setComposing(true)} style={btn('var(--color-accent)')}>
            <Send size={15} /> Email customers
          </button>
          <button onClick={() => setTexting(true)} style={btn('var(--color-accent)')}>
            <MessageSquare size={15} /> Text customers
          </button>
          <button onClick={() => doImport.mutate()} disabled={doImport.isPending} style={btn('var(--color-text-muted)')}>
            <Download size={15} /> {doImport.isPending ? 'Importing…' : 'Import from orders'}
          </button>
          <button onClick={() => setCreating(true)} style={btn('var(--color-primary)')}>+ New customer</button>
        </div>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 14 }}>
        Your storefront buyers in one place — lifetime spend, order history, and marketing opt-in.
        Import pulls every buyer from past orders (opt-in stays off until they consent).
      </p>
      {msg && <div style={{ ...card, padding: '8px 12px', marginBottom: 12, fontSize: 12.5, color: 'var(--color-text)' }}>{msg}</div>}

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, phone…" style={{ ...inp, marginBottom: 14, maxWidth: 360 }} />

      <div style={card}>
        {isLoading ? (
          <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>Loading…</p>
        ) : customers.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>
            No customers yet. Click <strong>Import from orders</strong> to pull in your storefront buyers, or add one manually.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {customers.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--color-bg)' }}>
                <button onClick={() => setViewing(c)} style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--color-text)' }}>
                    {c.name || <span style={{ color: 'var(--color-text-faint)' }}>(no name)</span>}
                    <span style={{ display: 'inline-flex', gap: 4, marginLeft: 8 }}>
                      {c.email_opt_in && <OptChip label="email" />}
                      {c.sms_opt_in && <OptChip label="SMS" />}
                      {c.marketing_opt_in && <OptChip label="marketing" />}
                    </span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                    {[c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                  </div>
                </button>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)' }}>{money(c.total_spent)}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-faint)' }}>
                    {c.order_count} order{c.order_count !== 1 ? 's' : ''}{c.last_order_at ? ` · ${new Date(c.last_order_at).toLocaleDateString()}` : ''}
                  </div>
                </div>
                <button onClick={() => setEditing(c)} title="Edit" style={iconBtn}><Pencil size={15} /></button>
                <button onClick={() => { if (confirm(`Remove ${c.name || 'this customer'}?`)) remove.mutate(c.id) }} title="Remove" style={{ ...iconBtn, color: '#dc2626' }}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {(creating || editing) && <CustomerModal customer={editing} onClose={() => { setCreating(false); setEditing(null) }} />}
      {viewing && <CustomerDetail customer={viewing} onClose={() => setViewing(null)} onEdit={() => { setEditing(viewing); setViewing(null) }} />}
      {composing && <CampaignModal optedIn={optedIn} onClose={() => setComposing(false)} />}
      {texting && <SMSModal optedIn={smsOptedIn} onClose={() => setTexting(false)} />}
    </main>
  )
}

function OptChip({ label }: { label: string }) {
  return <span style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: 'color-mix(in srgb, var(--color-primary) 16%, transparent)', color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</span>
}

function CustomerModal({ customer, onClose }: { customer: Customer | null; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    name: customer?.name ?? '', email: customer?.email ?? '', phone: customer?.phone ?? '',
    address: customer?.address ?? '', notes: customer?.notes ?? '',
    email_opt_in: customer?.email_opt_in ?? false, sms_opt_in: customer?.sms_opt_in ?? false,
    marketing_opt_in: customer?.marketing_opt_in ?? false,
  })
  const [error, setError] = useState('')
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm(f => ({ ...f, [k]: v }))

  const save = useMutation({
    mutationFn: () => {
      const payload: Partial<Customer> = { ...form, name: form.name.trim(), email: form.email.trim() }
      return customer ? customersApi.update(customer.id, payload) : customersApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customers'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed to save')),
  })

  return (
    <Overlay onClose={onClose}>
      <div style={{ ...card, width: 'min(520px, 94vw)', maxHeight: '90vh', overflow: 'auto' }}>
        <Header title={customer ? 'Edit customer' : 'New customer'} onClose={onClose} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Name"><input value={form.name} onChange={e => set('name', e.target.value)} style={inp} /></Field>
          <Field label="Email"><input value={form.email} onChange={e => set('email', e.target.value)} placeholder="buyer@email.com" style={inp} /></Field>
          <Field label="Phone"><input value={form.phone} onChange={e => set('phone', e.target.value)} style={inp} /></Field>
          <Field label="Address"><input value={form.address} onChange={e => set('address', e.target.value)} style={inp} /></Field>
        </div>
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: 'color-mix(in srgb, var(--color-primary) 5%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 14%, transparent)' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 8 }}>MARKETING CONSENT</div>
          <CheckRow label="Email opt-in" checked={form.email_opt_in} onChange={v => set('email_opt_in', v)} />
          <CheckRow label="SMS opt-in" checked={form.sms_opt_in} onChange={v => set('sms_opt_in', v)} />
          <CheckRow label="General marketing opt-in" checked={form.marketing_opt_in} onChange={v => set('marketing_opt_in', v)} />
        </div>
        <div style={{ marginTop: 12 }}><Field label="Notes"><textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} /></Field></div>
        {error && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ ...btn('transparent'), color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>Cancel</button>
          <button onClick={() => save.mutate()} disabled={save.isPending} style={btn('var(--color-primary)')}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Overlay>
  )
}

function CheckRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text)', padding: '3px 0', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} /> {label}
    </label>
  )
}

function CustomerDetail({ customer, onClose, onEdit }: { customer: Customer; onClose: () => void; onEdit: () => void }) {
  const { data, isLoading } = useQuery({ queryKey: ['customer-orders', customer.id], queryFn: () => customersApi.orders(customer.id).then(r => r.data.orders) })
  const orders = data ?? []
  const { data: resvData, isLoading: resvLoading } = useQuery({ queryKey: ['customer-reservations', customer.id], queryFn: () => customersApi.reservations(customer.id).then(r => r.data.reservations) })
  const reservations = resvData ?? []
  return (
    <Overlay onClose={onClose}>
      <div style={{ ...card, width: 'min(520px, 94vw)', maxHeight: '90vh', overflow: 'auto' }}>
        <Header title={customer.name || 'Customer'} onClose={onClose} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
          {customer.email && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={13} /> {customer.email}</span>}
          {customer.phone && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Phone size={13} /> {customer.phone}</span>}
          {customer.address && <span>{customer.address}</span>}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-border-tertiary)' }}>
          <Stat label="Lifetime spend" value={money(customer.total_spent)} />
          <Stat label="Orders" value={String(customer.order_count)} />
          <Stat label="Last order" value={customer.last_order_at ? new Date(customer.last_order_at).toLocaleDateString() : '—'} />
        </div>
        {customer.notes && <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--color-text-muted)' }}><span style={{ color: 'var(--color-text-faint)' }}>Notes: </span>{customer.notes}</div>}

        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 8 }}>ORDER HISTORY</div>
          {isLoading ? (
            <p style={{ fontSize: 12.5, color: 'var(--color-text-faint)' }}>Loading…</p>
          ) : orders.length === 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--color-text-faint)' }}>No storefront orders linked to this email.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {orders.map((o: CustomerOrder) => (
                <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '6px 10px', borderRadius: 7, background: 'var(--color-bg)' }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>{new Date(o.created_at).toLocaleDateString()}{o.status ? ` · ${o.status}` : ''}</span>
                  <span style={{ color: 'var(--color-text)', fontWeight: 600 }}>{money(o.total_amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 8 }}>PRE-ORDER RESERVATIONS</div>
          {resvLoading ? (
            <p style={{ fontSize: 12.5, color: 'var(--color-text-faint)' }}>Loading…</p>
          ) : reservations.length === 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--color-text-faint)' }}>No pre-order reservations for this customer.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {reservations.map((rv: CustomerReservation) => {
                const win = rv.expected_from ? `${rv.expected_from}${rv.expected_to && rv.expected_to !== rv.expected_from ? `–${rv.expected_to}` : ''}` : ''
                return (
                  <div key={rv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, fontSize: 12.5, padding: '6px 10px', borderRadius: 7, background: 'var(--color-bg)' }}>
                    <span style={{ color: 'var(--color-text)' }}>
                      <span style={{ fontWeight: 600 }}>{rv.quantity} {rv.unit_label || 'unit'}{rv.quantity !== 1 ? 's' : ''}</span> · {rv.item_name || 'item'}
                      {win && <span style={{ color: 'var(--color-text-faint)' }}> · {win}</span>}
                    </span>
                    <span style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{rv.status}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onEdit} style={{ ...btn('transparent'), color: 'var(--color-text)', border: '1px solid var(--color-border)' }}><Pencil size={14} /> Edit</button>
        </div>
      </div>
    </Overlay>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text)' }}>{value}</div>
      <div style={{ fontSize: 10.5, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
    </div>
  )
}

function CampaignModal({ optedIn, onClose }: { optedIn: number; onClose: () => void }) {
  const qc = useQueryClient()
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [sent, setSent] = useState<Campaign | null>(null)
  const [error, setError] = useState('')
  const { data: past } = useQuery({ queryKey: ['customer-campaigns'], queryFn: () => customersApi.campaigns().then(r => r.data.campaigns) })

  const send = useMutation({
    mutationFn: () => customersApi.sendCampaign({ subject: subject.trim(), body: body.trim() }).then(r => r.data),
    onSuccess: (c) => { setSent(c); qc.invalidateQueries({ queryKey: ['customer-campaigns'] }) },
    onError: (e) => setError(apiError(e, 'Send failed')),
  })

  return (
    <Overlay onClose={onClose}>
      <div style={{ ...card, width: 'min(560px, 94vw)', maxHeight: '90vh', overflow: 'auto' }}>
        <Header title="Email opted-in customers" onClose={onClose} />
        {sent ? (
          <div>
            <div style={{ padding: '14px 12px', borderRadius: 8, background: 'color-mix(in srgb, var(--color-primary) 8%, transparent)', fontSize: 13.5, color: 'var(--color-text)' }}>
              ✅ Sent to <strong>{sent.sent_count}</strong> of {sent.recipient_count} opted-in customer{sent.recipient_count !== 1 ? 's' : ''}.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={onClose} style={btn('var(--color-primary)')}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginBottom: 12 }}>
              Goes to your <strong>{optedIn}</strong> customer{optedIn !== 1 ? 's' : ''} who opted in to email. Each email
              includes a one-click unsubscribe link. (Customers opt in via the consent checkboxes on their record.)
            </div>
            <Field label="Subject"><input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Glenn mangoes ready this weekend" style={inp} /></Field>
            <div style={{ marginTop: 12 }}>
              <Field label="Message"><textarea value={body} onChange={e => setBody(e.target.value)} rows={7} placeholder="Hi! Our Glenn mangoes are peaking — come by the farm stand Sat–Sun 9–2, or reply to reserve a box." style={{ ...inp, resize: 'vertical' }} /></Field>
            </div>
            {error && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>{error}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={onClose} style={{ ...btn('transparent'), color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>Cancel</button>
              <button onClick={() => send.mutate()} disabled={send.isPending || optedIn === 0 || !subject.trim() || !body.trim()} style={btn('var(--color-primary)')}>
                {send.isPending ? 'Sending…' : `Send to ${optedIn}`}
              </button>
            </div>
            {(past ?? []).length > 0 && (
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--color-border-tertiary)' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 8 }}>RECENT SENDS</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {(past ?? []).slice(0, 6).map(c => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5, padding: '6px 10px', borderRadius: 7, background: 'var(--color-bg)' }}>
                      <span style={{ color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject || '(no subject)'}</span>
                      <span style={{ color: 'var(--color-text-faint)', flexShrink: 0 }}>{c.sent_count}/{c.recipient_count} · {new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Overlay>
  )
}

function SMSModal({ optedIn, onClose }: { optedIn: number; onClose: () => void }) {
  const [body, setBody] = useState('')
  const [kind, setKind] = useState<'blast' | 'availability'>('blast')
  const [topic, setTopic] = useState('')
  const [result, setResult] = useState<{ sent: number; total: number; configured: boolean } | null>(null)
  const [error, setError] = useState('')
  const segments = Math.max(1, Math.ceil((body.length + 24) / 153)) // + opt-out line, ~GSM segment estimate

  const send = useMutation({
    mutationFn: () => customersApi.sendSMS({ body: body.trim(), kind }).then(r => r.data),
    onSuccess: (r) => setResult({ sent: r.sent_count, total: r.recipient_count, configured: r.configured }),
    onError: (e) => setError(apiError(e, 'Send failed')),
  })
  const draft = useMutation({
    mutationFn: () => customersApi.draftSMS(topic.trim()).then(r => r.data),
    onSuccess: (r) => { setBody(r.draft); setError('') },
    onError: (e) => setError(apiError(e, 'Drafting failed')),
  })

  const seg = (k: 'blast' | 'availability', label: string) => (
    <button type="button" onClick={() => setKind(k)} style={{
      flex: 1, padding: '7px 0', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
      border: '1px solid var(--color-border)', background: kind === k ? 'var(--color-primary)' : 'transparent',
      color: kind === k ? '#fff' : 'var(--color-text-muted)',
    }}>{label}</button>
  )

  return (
    <Overlay onClose={onClose}>
      <div style={{ ...card, width: 'min(520px, 94vw)', maxHeight: '90vh', overflow: 'auto' }}>
        <Header title="Text opted-in customers" onClose={onClose} />
        {result ? (
          <div>
            <div style={{ padding: '14px 12px', borderRadius: 8, background: 'color-mix(in srgb, var(--color-primary) 8%, transparent)', fontSize: 13.5, color: 'var(--color-text)' }}>
              {result.configured
                ? <>✅ Texted <strong>{result.sent}</strong> of {result.total} opted-in customer{result.total !== 1 ? 's' : ''}.</>
                : <>⚠️ SMS isn’t set up yet — nothing was sent. ({result.total} customer{result.total !== 1 ? 's are' : ' is'} opted in and ready once Twilio is connected.)</>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={onClose} style={btn('var(--color-primary)')}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginBottom: 12 }}>
              Goes to your <strong>{optedIn}</strong> customer{optedIn !== 1 ? 's' : ''} who opted in to SMS and have a phone.
              An opt-out line (“Reply STOP to opt out.”) is added automatically, and anyone who’s texted STOP is skipped.
              (Customers opt in via the consent checkboxes on their record.)
            </div>
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
              {seg('blast', 'General')}
              {seg('availability', 'Availability')}
            </div>
            <div style={{ padding: 10, borderRadius: 8, border: '1px dashed var(--color-border)', marginBottom: 12 }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Sparkles size={13} /> Draft with AI
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input value={topic} onChange={e => setTopic(e.target.value)}
                  placeholder="e.g. Kent mangoes ready this weekend, farm stand 9–2"
                  style={{ ...inp, flex: 1 }} />
                <button type="button" onClick={() => draft.mutate()} disabled={draft.isPending || !topic.trim()} style={btn('var(--color-accent)')}>
                  {draft.isPending ? '…' : 'Draft'}
                </button>
              </div>
            </div>
            <Field label="Message">
              <textarea value={body} onChange={e => setBody(e.target.value)} rows={5}
                placeholder="Glenn mangoes are ready! Farm stand open Sat–Sun 9–2, or reply to reserve a box." style={{ ...inp, resize: 'vertical' }} />
            </Field>
            <div style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 4 }}>
              {body.length} chars · ~{segments} SMS segment{segments !== 1 ? 's' : ''} per recipient
            </div>
            {error && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>{error}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={onClose} style={{ ...btn('transparent'), color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>Cancel</button>
              <button onClick={() => send.mutate()} disabled={send.isPending || optedIn === 0 || !body.trim()} style={btn('var(--color-primary)')}>
                {send.isPending ? 'Sending…' : `Text ${optedIn}`}
              </button>
            </div>
          </>
        )}
      </div>
    </Overlay>
  )
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      {children}
    </div>
  )
}
function Header({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>{title}</h2>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={18} /></button>
    </div>
  )
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block' }}><span style={lbl}>{label}</span>{children}</label>
}
